import { Avatar, avatarColor, Badge, Card } from '@parsina/framework';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { conversationTimeLabel, formatUnread, roleLabelKey } from './Messages.shared';

// The desktop conversation row — local to this screen on purpose (§1.1: no
// component straddles the two surfaces; the phone has its own card). Kit Card /
// Avatar / Badge only, so it inherits the surface, the focus ring and the real
// button semantics rather than re-deriving them.
//
// Every user-content node is `dir="auto"`: a Persian name, an English one and a
// mixed message preview all sit in the same column and must each resolve their
// own direction.

const ConversationRow = ({ row, active, onOpen }) => {
  const { t, formatNumber, formatTime, formatDate } = useTranslation();

  const name = row.peerDisplayName || t('messages.unknownContact');
  const timeLabel = conversationTimeLabel(row.lastActivityTs, { t, formatTime, formatDate });
  const unreadLabel = formatUnread(row.unread, formatNumber);
  const preview = row.lastEvent?.body || '';

  return (
    <Card
      onClick={() => onOpen(row)}
      data-testid={`messages-list-row-${row.roomId || row.key}`}
      data-active={active ? 'true' : 'false'}
      aria-current={active ? 'true' : undefined}
      style={{
        padding: '0.75rem',
        // Card scale (14px), the same as its phone twin — 12px is off the token
        // scale (10px controls / 14px cards / 20px hero).
        borderRadius: '0.875rem',
        borderColor: active ? C.navy : C.border,
        background: active ? C.bg : C.card,
        boxShadow: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', minWidth: 0 }}>
        <Avatar name={name} size={38} bg={avatarColor(name)} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', minWidth: 0 }}>
            <span
              dir="auto"
              style={{
                flex: 1, minWidth: 0,
                fontSize: '0.875rem', fontWeight: row.unread > 0 ? 800 : 700,
                color: C.textPrimary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {name}
            </span>
            {timeLabel && (
              <span style={{ fontSize: '0.6875rem', color: C.textMuted, flexShrink: 0 }}>
                {timeLabel}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.1875rem', minWidth: 0 }}>
            <span
              dir="auto"
              style={{
                flex: 1, minWidth: 0,
                fontSize: '0.75rem', color: C.textSecondary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {preview || t(roleLabelKey(row))}
            </span>
            {unreadLabel && (
              // `variant="count"` rather than a style override: the kit owns the
              // unread chip's whole geometry, including the tabular-nums and
              // min-width that stop «۱» and «۹+» rendering at different sizes.
              // Navy, never red — an unread message is attention, not alarm.
              <Badge
                variant="count"
                data-testid={`messages-list-row-${row.roomId || row.key}-unread`}
                aria-label={t('messages.unreadBadge', { count: unreadLabel })}
              >
                {unreadLabel}
              </Badge>
            )}
          </div>

          {/* A patient with no account yet: say the message will wait, rather
              than letting a normal-looking row imply it was delivered. */}
          {row.reachable === false && (
            <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: '0.1875rem' }}>
              {t('messages.notOnApp')}
            </div>
          )}
          {!row.started && row.reachable !== false && (
            <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: '0.1875rem' }}>
              {t('messages.startConversation')}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ConversationRow;
